<!-- src/components/sections/RealityCheck.svelte -->
<script>
  import Button from '../common/Button.svelte';
  import offer from '../../content/reality-check.json';
  import { BOOKING_URL, REALITY_CHECK_PRICE } from '../../lib/config.js';

  // When false (default), shows the "see full details" link to the dedicated page.
  // The dedicated page sets full=true to hide that self-link.
  export let full = false;
</script>

<section class="offer" id="reality-check" aria-labelledby="offer-heading">
  <div class="container">
    <div class="offer_card">
      <p class="eyebrow">The offer</p>
      <h2 id="offer-heading" class="offer_name marker-font">{offer.name}</h2>
      {#if offer.acronym}
        <p class="acronym">{offer.acronym}</p>
      {/if}
      <p class="tagline">{offer.tagline}</p>

      <h3 class="how_heading">{offer.how_heading}</h3>
      <ol class="steps">
        {#each offer.steps as step}
          <li class="step">
            <span class="step_num" aria-hidden="true">{step.n}</span>
            <div class="step_body">
              <p class="step_title">{step.title}</p>
              <p class="step_text">{step.text}</p>
            </div>
          </li>
        {/each}
      </ol>

      <div class="price_row">
        <div class="price_block">
          <span class="price">{REALITY_CHECK_PRICE}</span>
          <span class="price_label">{offer.price_label}</span>
        </div>
        <p class="price_note">{offer.price_note}</p>
      </div>

      <p class="ladder">{offer.ladder}</p>

      {#if full}
        <p class="for_who">{offer.for_who}</p>
      {/if}

      <div class="offer_cta">
        <Button href={BOOKING_URL} variant="primary" size="large">{offer.cta}</Button>
        {#if !full}
          <a class="details_link" href="/ai-lift">See how it works →</a>
        {/if}
      </div>
    </div>
  </div>
</section>

<style>
  .offer {
    background: var(--color-background);
    padding: 5rem 0;
  }

  .container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .offer_card {
    background: var(--color-card-bg);
    border: 1px solid var(--color-card-hover);
    border-radius: 16px;
    padding: 3rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 0.9rem;
    color: var(--color-secondary);
    margin: 0 0 0.5rem;
    font-weight: 600;
  }

  .offer_name {
    font-size: 2.75rem;
    line-height: 1.1;
    color: var(--color-text);
    margin: 0 0 1rem;
  }

  .acronym {
    font-size: 0.95rem;
    color: var(--color-primary);
    letter-spacing: 0.02em;
    margin: 0 0 1.25rem;
    font-weight: 500;
  }

  .tagline {
    font-size: 1.35rem;
    line-height: 1.5;
    color: var(--color-text);
    margin: 0 0 2.5rem;
    max-width: 760px;
  }

  .how_heading {
    font-size: 1.4rem;
    color: var(--color-primary);
    margin: 0 0 1.5rem;
  }

  .steps {
    list-style: none;
    padding: 0;
    margin: 0 0 2.5rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    counter-reset: none;
  }

  .step {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .step_num {
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 50%;
    background: var(--color-primary);
    color: var(--color-background);
    font-weight: 700;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .step_title {
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--color-text);
    margin: 0.25rem 0 0.4rem;
  }

  .step_text {
    font-size: 1.05rem;
    line-height: 1.55;
    color: var(--color-text-muted);
    margin: 0;
  }

  .price_row {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    padding: 1.5rem 0;
    border-top: 1px solid var(--color-card-hover);
    border-bottom: 1px solid var(--color-card-hover);
    margin-bottom: 1.75rem;
  }

  .price_block {
    display: flex;
    flex-direction: column;
  }

  .price {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-text);
    line-height: 1.1;
  }

  .price_label {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
  }

  .price_note {
    font-size: 1.05rem;
    color: var(--color-text-muted);
    margin: 0;
    flex: 1;
    min-width: 240px;
  }

  .ladder {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--color-text);
    margin: 0 0 1rem;
  }

  .for_who {
    font-size: 1.05rem;
    line-height: 1.6;
    color: var(--color-text-muted);
    margin: 0 0 1.5rem;
  }

  .offer_cta {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-top: 1.5rem;
  }

  .details_link {
    font-size: 1.05rem;
    color: var(--color-primary);
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .offer {
      padding: 3.5rem 0;
    }

    .offer_card {
      padding: 2rem 1.5rem;
    }

    .offer_name {
      font-size: 2.1rem;
    }

    .tagline {
      font-size: 1.15rem;
    }

    .steps {
      grid-template-columns: 1fr;
      gap: 1.25rem;
    }
  }
</style>
